/**
 * @file BoardMobile.tsx
 * @description Доска на телефоне и планшете: стадии — пилюлями сверху, под
 * ними одна колонка на экран (phase10.5; phase19.31 — разбор A, «Доска»).
 *
 * Перетаскивать карточки пальцем по длинным колонкам неудобно, поэтому до
 * 1024 доска — выбор стадии со счётчиками и список карточек выбранной
 * стадии (одна колонка на экран). Выбор — `SelectField`, а не пилюли: пилюли
 * в кабинете переносятся на новую строку (решение приёмки 2026-10-02), и
 * 13 стадий воронки заняли бы на 360 семь строк — весь первый экран.
 * Стадию меняют в карточке заказа. Строка списка — ссылка 48px+, номер
 * обычным шрифтом с табличными цифрами.
 */

import { useState } from "react";
import { Link } from "react-router";

import type { BoardType, StageRow } from "./board-types";
import { OrderRowChips } from "../OrderRowChips";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import { SelectField } from "../../ui/SelectField";
import { RETURN_TYPE_LABEL, formatMoney, type OrderDto } from "../../../lib/api-orders";
import type { ReturnDto } from "../../../lib/api-returns";
import { cn } from "@/lib/utils";

interface Props {
  boardType: BoardType;
  stages: StageRow[];
  orders: OrderDto[];
  returns: ReturnDto[];
  /** База адреса заказов: `/shop/:slug/orders`. */
  ordersBase: string;
}

/** Строка списка: три линии, вся — ссылка. */
const ROW_CLASS =
  "flex min-h-18 flex-col justify-center gap-0.5 px-3 py-2 outline-none transition-colors hover:bg-[var(--row-hover)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

export function BoardMobile({ boardType, stages, orders, returns, ordersBase }: Props) {
  const [picked, setPicked] = useState<string | null>(null);
  const stageId = picked && stages.some((s) => s.id === picked) ? picked : (stages[0]?.id ?? "");
  const stageOrders = boardType === "orders" ? orders.filter((o) => o.stage?.id === stageId) : [];
  const stageReturns = boardType === "returns" ? returns.filter((r) => r.stage?.id === stageId) : [];
  const count = (id: string) =>
    boardType === "orders" ? orders.filter((o) => o.stage?.id === id).length : returns.filter((r) => r.stage?.id === id).length;
  const empty = stageOrders.length === 0 && stageReturns.length === 0;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <SelectField
        ariaLabel="Стадия"
        value={stageId}
        onChange={setPicked}
        options={stages.map((s) => ({ value: s.id, label: `${s.name} · ${count(s.id)}` }))}
        className="w-full"
      />
      {empty ? (
        <p className="type-body rounded-xl bg-surface-lowest px-4 py-6 text-center text-on-surface-variant">
          На этой стадии сейчас пусто
        </p>
      ) : (
        <ul className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)]")}>
          {stageOrders.map((o) => (
            <li key={o.id}>
              <Link to={`${ordersBase}/${o.id}`} className={ROW_CLASS}>
                <span className="flex items-center justify-between gap-2">
                  <span className="type-body font-medium tabular-nums">{o.number}</span>
                  <span className="type-body whitespace-nowrap font-semibold tabular-nums">{formatMoney(o.totalGross)} ₽</span>
                </span>
                <span className="type-caption truncate text-on-surface-variant">{o.customerDisplayName ?? "Без имени"}</span>
                {/* Стикеры «чей ход» и «нужно решение» — те же, что на карточке доски. */}
                <OrderRowChips order={o} short className="flex items-center gap-1" />
              </Link>
            </li>
          ))}
          {stageReturns.map((r) => (
            <li key={r.id}>
              <Link to={`${ordersBase}/${r.orderId}`} className={ROW_CLASS}>
                <span className="flex items-center justify-between gap-2">
                  <span className="type-body font-medium tabular-nums">{r.orderNumber}</span>
                  <span className="type-body whitespace-nowrap font-semibold tabular-nums">{formatMoney(r.refundAmount)} ₽</span>
                </span>
                <span className="type-caption truncate text-on-surface-variant">{RETURN_TYPE_LABEL[r.type]}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
